import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Dropdown, MenuItem } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { FaCouch, FaFillDrip, FaHome, FaRegClock, FaSun } from 'react-icons/fa';
import momentPropTypes from 'react-moment-proptypes';
import moment from 'moment';

import intlShape from '@/shared/utils/intlShape';
import HoverToggle from '@/shared/components/HoverToggle';
import { stateServicePropType } from '@/shared/services/StateService';
import DataCacheService from '@/shared/services/DataCacheService';
import AuthService from '@/shared/services/AuthService';
import { canCreateRegistrationForUser } from '@/shared/components/registration/registrationHelpers';
import messages from '../reportingMessages';

const HoursPerEmployeeReportDropdown = ({
  value,
  startDate,
  endDate,
  userId,
  groupNr,
  groupBy,
  stateService,
  onAddRegistration,
  onToggleDetails,
  onChangeToDayView,
  onUpdateHomeWorkingHours,
  onOpenFillGapsRegistrationModal,
  missing,
  expected,
  homeWorking,
  hasDetails,
  intl,
}) => {
  const currentUserId = AuthService.getUserId();
  const canCreateRegistration = canCreateRegistrationForUser(userId);
  const canSetHomeWorkingHours =
    groupBy === 1 &&
    hasDetails &&
    (currentUserId === userId ||
      AuthService.hasPermission({ functionality: 'Users', privilege: 'Updates' }));
  return (
    <td
      className={classnames(
        'text-right hours',
        { 'text-muted': !value },
        {
          weekend:
            endDate.toISOString() === startDate.toISOString() &&
            (endDate.isoWeekday() === 6 || endDate.isoWeekday() === 7),
        },
      )}
      onClick={onToggleDetails}
    >
      <Dropdown id={`Dropdown-${userId}-${groupNr}`}>
        <HoverToggle bsRole="toggle" />
        <span className="info">
          {!value && (value !== 0 || (hasDetails && expected === 0)) ? null : value.minuteToTime()}
        </span>
        <span className="icons">
          {homeWorking ? (
            <i
              className="fa fa-home fa-fw homeWorking"
              title={intl.formatMessage(messages.HomeWorkingHoursTooltip, {
                value: homeWorking.minuteToTime(),
              })}
            />
          ) : null}
          {missing ? (
            <i
              className="fa fa-arrow-circle-down fa-fw missing"
              title={intl.formatMessage(messages.MissingHoursTooltip, {
                value: missing.minuteToTime(),
              })}
            />
          ) : null}
        </span>

        <Dropdown.Menu className="unselectable">
          <MenuItem
            eventKey="0"
            onSelect={() =>
              stateService.gotoRegistrationOverview({
                userId,
                dateFrom: startDate,
                dateUntil: endDate,
                period: 'all',
              })
            }
          >
            <i className="fa fa-th fa-fw margin5R" />
            <FormattedMessage {...messages.OpenRegistrationOverview} />
          </MenuItem>
          {groupBy > 1 ? (
            <MenuItem eventKey="1" onSelect={() => onChangeToDayView(startDate, endDate)}>
              <i className="fa fa-search-plus fa-fw margin5R" />
              <FormattedMessage {...messages.ZoomInToDayView} />
            </MenuItem>
          ) : null}
          {canCreateRegistration ? (
            <MenuItem
              eventKey="2"
              onSelect={() => onAddRegistration({ type: 0, userId, date: startDate })}
            >
              <FaRegClock className="react-icon fa-fw margin5R" />
              <FormattedMessage {...messages.AddRegistration} />
            </MenuItem>
          ) : null}
          {canCreateRegistration ? (
            <MenuItem
              eventKey="3"
              onSelect={() => onAddRegistration({ type: 3, userId, date: startDate })}
            >
              <FaSun className="react-icon fa-fw margin5R" />
              <FormattedMessage {...messages.AddAbsence} />
            </MenuItem>
          ) : null}
          {canCreateRegistration && DataCacheService.timeTablesEnabled() ? (
            <MenuItem
              eventKey="4"
              onSelect={() => onAddRegistration({ type: 4, userId, date: startDate })}
            >
              <FaCouch className="react-icon fa-fw margin5R" />
              <FormattedMessage {...messages.AddRecuperation} />
            </MenuItem>
          ) : null}
          {canCreateRegistration && hasDetails && missing > 0 ? (
            <MenuItem eventKey="5" onSelect={() => onOpenFillGapsRegistrationModal(0)}>
              <FaFillDrip className="react-icon fa-fw margin5R" />
              <FormattedMessage {...messages.FillPeriodWithWork} />
            </MenuItem>
          ) : null}
          {canCreateRegistration && hasDetails && missing > 0 ? (
            <MenuItem eventKey="5" onSelect={() => onOpenFillGapsRegistrationModal(4)}>
              <FaFillDrip className="react-icon fa-fw margin5R" />
              <FormattedMessage {...messages.FillPeriodWithRecup} />
            </MenuItem>
          ) : null}
          {canSetHomeWorkingHours && expected > 0 && homeWorking !== expected && (
            <MenuItem
              eventKey="7"
              onSelect={() => onUpdateHomeWorkingHours(userId, startDate, expected)}
            >
              <FaHome className="react-icon fa-fw margin5R" />
              <FormattedMessage {...messages.RegisterWorkingFromHome} />
            </MenuItem>
          )}
          {canSetHomeWorkingHours && homeWorking > 0 && (
            <MenuItem eventKey="8" onSelect={() => onUpdateHomeWorkingHours(userId, startDate, 0)}>
              <FaHome className="react-icon fa-fw margin5R" />
              <FormattedMessage {...messages.ClearWorkingFromHome} />
            </MenuItem>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </td>
  );
};

HoursPerEmployeeReportDropdown.propTypes = {
  value: PropTypes.number,
  startDate: momentPropTypes.momentObj,
  endDate: momentPropTypes.momentObj,
  userId: PropTypes.string.isRequired,
  groupNr: PropTypes.number.isRequired,
  groupBy: PropTypes.number.isRequired,
  stateService: stateServicePropType.isRequired,
  onAddRegistration: PropTypes.func.isRequired,
  onToggleDetails: PropTypes.func.isRequired,
  onChangeToDayView: PropTypes.func.isRequired,
  onUpdateHomeWorkingHours: PropTypes.func.isRequired,
  onOpenFillGapsRegistrationModal: PropTypes.func.isRequired,
  missing: PropTypes.number,
  expected: PropTypes.number,
  homeWorking: PropTypes.number,
  hasDetails: PropTypes.bool,
  intl: intlShape.isRequired,
};
HoursPerEmployeeReportDropdown.defaultProps = {
  value: 0,
  startDate: null,
  endDate: null,
  missing: 0,
  expected: 0,
  homeWorking: 0,
  hasDetails: false,
};
export default HoursPerEmployeeReportDropdown;
